import beepSfx from "@/audio/new-message-ding.mp3";
import drumSfx from "@/audio/new-message-drum.mp3";
import pedalSteelSfx from "@/audio/new-message-pedal-steel.mp3";
import { createLogger } from "@/logger";
import {
  DEFAULT_NEW_MESSAGE_SOUND_ID,
  isAudibleInboundItem,
  MAX_NEW_MESSAGE_SOUND_VOLUME,
  type NewMessageSoundId,
  type NewMessageSoundScope,
} from "@/models/newMessageSoundRules";
import { hasEnabledReadPermission, preferenceForFeed } from "@/store/feedSelectors";
import type { Item } from "@/store/types";
import { isElectron } from "@/utils";

const log = createLogger("appsync");

/** Mirrors Electron's main-process cooldown so a burst of messages produces a single ding. */
const PLAY_COOLDOWN_MS = 1000;

/** Cooldown timestamp shared across tabs so N open tabs produce one ding per burst, not N. */
const LAST_PLAYED_AT_KEY = "newMessageSoundLastPlayedAt";

/** The file each sound plays. A `Record`, so a new sound id fails typecheck until it has a file. */
const SOUND_URLS: Record<NewMessageSoundId, string> = {
  beep: beepSfx,
  pedalSteel: pedalSteelSfx,
  drum: drumSfx,
};

let soundScope: NewMessageSoundScope = "off";
let soundId: NewMessageSoundId = DEFAULT_NEW_MESSAGE_SOUND_ID;
let soundVolume = MAX_NEW_MESSAGE_SOUND_VOLUME;
let myWorkspaceMembershipId: string | null = null;
let lastPlayedAtThisTab = 0;
/** The one element previews play through, so a new preview cuts the last one off rather than stacking on it. */
let previewAudio: HTMLAudioElement | null = null;

function lastPlayedAt(): number {
  try {
    return Math.max(lastPlayedAtThisTab, Number(window.localStorage.getItem(LAST_PLAYED_AT_KEY)) || 0);
  } catch {
    return lastPlayedAtThisTab;
  }
}

function recordPlayedAt(now: number): void {
  lastPlayedAtThisTab = now;
  try {
    window.localStorage.setItem(LAST_PLAYED_AT_KEY, String(now));
  } catch {
    // localStorage unavailable: the cooldown falls back to per-tab.
  }
}

/** The sound's audio element at `volume` percent. */
function soundAudio(id: NewMessageSoundId, volume: number): HTMLAudioElement {
  const audio = new Audio(SOUND_URLS[id]);
  audio.volume = volume / 100;
  return audio;
}

/** Starts `audio`, swallowing the rejection a browser's autoplay block raises: every sound here is best-effort. */
function playBestEffort(audio: HTMLAudioElement): void {
  audio.play().catch(() => {
    // Browsers block autoplay until the user first interacts with the page.
    log.debug("New-message sound not played (autoplay blocked)");
  });
}

/**
 * Mirrors the `newMessageSoundScope`, `newMessageSoundId` and `newMessageSoundVolume` web settings, plus the
 * workspace membership id both scope lookups need, for this module's non-React caller in the AppSync pipeline.
 * `WebSettingsProvider` keeps them in sync.
 */
export function setNewMessageSoundConfig(config: {
  scope: NewMessageSoundScope;
  soundId: NewMessageSoundId;
  volume: number;
  myWorkspaceMembershipId: string | null;
}): void {
  soundScope = config.scope;
  soundId = config.soundId;
  soundVolume = config.volume;
  myWorkspaceMembershipId = config.myWorkspaceMembershipId;
}

/**
 * Plays `id` once at `volume` percent, as the profile page's preview of a sound or volume choice.
 *
 * Ignores the scope and the membership, so a member can hear the sounds before turning the ding on, and never reads
 * or records the ding's cooldown, so a preview never holds back a real ding. A new preview stops the one still
 * playing. The caller only renders outside the Electron shell, so this does not check for it.
 */
export function previewNewMessageSound(id: NewMessageSoundId, volume: number): void {
  if (previewAudio) {
    previewAudio.pause();
    previewAudio.currentTime = 0;
  }
  previewAudio = soundAudio(id, volume);
  playBestEffort(previewAudio);
}

/**
 * Plays the member's chosen sound at their chosen volume when a processed AppSync batch contains a new inbound
 * message inside their chosen scope.
 *
 * No-op when the `newMessageSoundScope` setting is off, or inside the Electron shell — its main
 * process already plays its own ding (Beep) for push notifications, and a second ding here would double it.
 * Also a no-op before the workspace membership is known: both remaining scopes are keyed on it, and
 * staying silent beats dinging for every driver while the scope cannot be evaluated.
 *
 * Under `allDrivers` the gate is the local permission records — an enabled `read` permission on the
 * item's feed held by this membership. AppSync keeps those rows current, so a grant or a revocation
 * takes effect on the next message without a reload. The membership filter is not optional:
 * permission events are published to the workspace-wide channel, so the local table also holds other
 * members' rows.
 */
export function playSoundForNewInboundItems(items: Item[], myAccountId: string): void {
  if (soundScope === "off" || isElectron) return;
  const membershipId = myWorkspaceMembershipId;
  if (membershipId === null) return;
  // One cache per batch and per lookup, so N items on the same feed cost one store scan rather
  // than N.
  const myDriverFeeds = new Map<string, boolean>();
  const isMyDriverFeed = (feedId: string): boolean => {
    const cached = myDriverFeeds.get(feedId);
    if (cached !== undefined) return cached;
    const isMine = preferenceForFeed(feedId, membershipId, { notifyingOnly: true }) !== null;
    myDriverFeeds.set(feedId, isMine);
    return isMine;
  };
  const readableFeeds = new Map<string, boolean>();
  const hasReadPermission = (feedId: string): boolean => {
    const cached = readableFeeds.get(feedId);
    if (cached !== undefined) return cached;
    const readable = hasEnabledReadPermission(feedId, membershipId);
    readableFeeds.set(feedId, readable);
    return readable;
  };
  const ctx = { myAccountId, scope: soundScope, isMyDriverFeed, hasReadPermission };
  if (!items.some((item) => isAudibleInboundItem(item, ctx))) return;
  const now = Date.now();
  if (now - lastPlayedAt() < PLAY_COOLDOWN_MS) return;
  recordPlayedAt(now);
  playBestEffort(soundAudio(soundId, soundVolume));
}
